import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { Input } from "../../../common/components/Input";
import OrganizationInputStyle from "../../../common/components/Input/OrganizationInputStyle";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { ButtonText } from "../../../common/components/Button";
import { FloatingTooltip } from "../../../common/components/Tooltip/FloatingTooltip";
import ModalConfirmation from "../../../common/components/Modal/ModalConfirmation";
import {
  closeConfirmationModal,
  openConfirmationModal,
} from "../../../common/state/slice/modal/modalSlice";
import { setAlert } from "../../alert/state/alertsSlice";
import type { IUser } from "../../../model/model";
import {
  fetchLrsConfig,
  fetchLrsStatus,
  getLrsConfig,
  getLrsStatus,
  replayLrsHistory,
  saveLrsConfig,
  testLrsConnection,
} from "../state/lrsSlice";
import type { ILrsConfigPatch, ILrsConnectionTest, LrsScopeMode } from "../state/lrsSlice";
import { downloadLrsDocument } from "../downloadLrsDocument";
import type { LrsDocument } from "../downloadLrsDocument";
import ModuleScopePicker, { summarizeModuleScope } from "./ModuleScopePicker";
import LrsConnectionDialog from "./LrsConnectionDialog";
import LrsDataDialog from "./LrsDataDialog";
import { useProjectGroupsWithProjects } from "../../reports/hooks/useProjectGroupsWithProjects";
import { lrsRequestErrorMessage, lrsSendErrorMessage, lrsTestMessage } from "../lrsMessages";

export const LRS_COMPONENT_ID = "lrsExportComponent";
const STATUS_POLL_MS = 10000;
const DISABLE_CONFIRM_ID = "lrsDisableExportConfirm";
const REPLAY_CONFIRM_ID = "lrsReplayHistoryConfirm";
const PREFIX = "pages.profile.organization.lrs";

interface LrsExportSectionProps {
  currentUser: IUser;
}

const toDateInput = (iso: string | null | undefined) => {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const separatorClassName = "border-r-[1px] mr-2 pr-2 border-gray-300";

const LrsExportSection = ({ currentUser }: LrsExportSectionProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const dispatch = useAppDispatch();
  const config = useAppSelector(getLrsConfig);
  const status = useAppSelector(getLrsStatus);

  const [connectionOpen, setConnectionOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastTest, setLastTest] = useState<ILrsConnectionTest | null>(null);
  const [replayDate, setReplayDate] = useState("");
  const projectGroups = useProjectGroupsWithProjects();

  const selectedProjects = (config?.projects ?? []).map((p) => Number(p.id));
  const scopeSummary = summarizeModuleScope(selectedProjects, projectGroups.pgList);

  useEffect(() => {
    void dispatch(fetchLrsStatus({ componentId: LRS_COMPONENT_ID })).then(() =>
      dispatch(fetchLrsConfig({})),
    );
  }, []);

  useEffect(() => {
    if (!config?.id) {
      return undefined;
    }
    const tick = () => {
      if (document.visibilityState === "visible") {
        void dispatch(fetchLrsStatus({}));
      }
    };
    const id = window.setInterval(tick, STATUS_POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [config?.id]);

  const refreshStatus = () => dispatch(fetchLrsStatus({}));

  const save = (patch: ILrsConfigPatch, successKey: string) =>
    dispatch(saveLrsConfig({ componentId: LRS_COMPONENT_ID, patch })).then((action) => {
      if (saveLrsConfig.fulfilled.match(action)) {
        dispatch(setAlert({ type: "success", msg: t(successKey) }));
        void refreshStatus();
        return true;
      }
      dispatch(setAlert({ type: "danger", msg: lrsRequestErrorMessage(t, action.payload) }));
      return false;
    });

  const runConnectionTest = () => {
    setTesting(true);
    void dispatch(testLrsConnection({})).then((action) => {
      setTesting(false);
      if (testLrsConnection.fulfilled.match(action)) {
        setLastTest(action.payload);
      } else {
        dispatch(setAlert({ type: "danger", msg: lrsRequestErrorMessage(t, action.payload) }));
      }
    });
  };

  const download = (kind: LrsDocument) => {
    downloadLrsDocument(kind).catch(() =>
      dispatch(setAlert({ type: "danger", msg: t(`${PREFIX}.errors.generic`) })),
    );
  };

  const openReplay = () => {
    setReplayDate(toDateInput(config?.emit_from));
    dispatch(openConfirmationModal(REPLAY_CONFIRM_ID));
  };

  const confirmReplay = () => {
    if (!replayDate) {
      return;
    }
    dispatch(closeConfirmationModal(REPLAY_CONFIRM_ID));
    void dispatch(
      replayLrsHistory({
        componentId: LRS_COMPONENT_ID,
        emit_from: new Date(replayDate).toISOString(),
      }),
    ).then((action) => {
      if (replayLrsHistory.fulfilled.match(action)) {
        dispatch(setAlert({ type: "success", msg: t("alerts.lrsReplayStarted") }));
        void refreshStatus();
      } else {
        dispatch(setAlert({ type: "danger", msg: lrsRequestErrorMessage(t, action.payload) }));
      }
    });
  };

  const confirmDisable = () => {
    dispatch(closeConfirmationModal(DISABLE_CONFIRM_ID));
    void save({ enabled: false }, "alerts.lrsExportDisabled");
  };

  const confirmScope = (next: number[], mode: LrsScopeMode) => {
    void save(
      mode === "selected" ? { scope_mode: mode, projects: next } : { scope_mode: mode },
      "alerts.lrsConfigSaved",
    ).then((ok) => {
      if (ok) {
        setPickerOpen(false);
      }
    });
  };

  const isConfigured = Boolean(config?.endpoint && config?.has_credentials);
  const isPaused = Boolean(config?.enabled && config.status !== "active");

  const statusLabel = (() => {
    if (!config || !isConfigured) {
      return t(`${PREFIX}.statusNotConfigured`);
    }
    if (!config.enabled) {
      return t(`${PREFIX}.statusDisabled`);
    }
    return t(`${PREFIX}.status_${config.status}`);
  })();

  const sendError = config ? lrsSendErrorMessage(t, config) : null;
  const retrying = Boolean(
    config?.enabled && config.status === "active" && config.consecutive_failures > 0,
  );
  const emissionSuspended = Boolean(status && !status.emissionEnabled && config?.enabled);
  const checkedAt = status?.cursor?.checkedAt ?? config?.last_translated_at ?? null;

  const statusDetail = (() => {
    if (emissionSuspended) {
      return { text: t(`${PREFIX}.emissionSuspended`), warn: true };
    }
    if (sendError && (isPaused || retrying)) {
      return {
        text: retrying ? `${sendError} · ${t(`${PREFIX}.retrying`)}` : sendError,
        warn: true,
      };
    }
    if (config?.enabled) {
      return {
        text: checkedAt
          ? t(`${PREFIX}.liveHint`, {
              time: new Date(checkedAt).toLocaleTimeString(locale),
            })
          : t(`${PREFIX}.liveHintNoCheck`),
        warn: false,
      };
    }
    return null;
  })();

  const statusActions = (() => {
    if (!config || !isConfigured) {
      return <></>;
    }
    if (!config.enabled) {
      return (
        <button
          type="button"
          className="btn-primary-fill whitespace-nowrap"
          onClick={() => void save({ enabled: true, resume: true }, "alerts.lrsExportEnabled")}
        >
          {t(`${PREFIX}.enable`)}
        </button>
      );
    }
    if (isPaused) {
      return (
        <div className="flex items-center whitespace-nowrap">
          <div className={separatorClassName}>
            <ButtonText
              type="button"
              text={t(`${PREFIX}.disableShort`)}
              onClick={() => dispatch(openConfirmationModal(DISABLE_CONFIRM_ID))}
            />
          </div>
          <button
            type="button"
            className="btn-primary-fill whitespace-nowrap"
            onClick={() => void save({ resume: true }, "alerts.lrsExportResumed")}
          >
            {t(`${PREFIX}.resume`)}
          </button>
        </div>
      );
    }
    return (
      <button
        type="button"
        className="btn-danger-fill whitespace-nowrap"
        onClick={() => dispatch(openConfirmationModal(DISABLE_CONFIRM_ID))}
      >
        {t(`${PREFIX}.disable`)}
      </button>
    );
  })();

  const scopeText = (() => {
    if (config?.scope_mode !== "selected") {
      return { text: t(`${PREFIX}.scopeAllSummary`), warn: false };
    }
    if (!selectedProjects.length) {
      return { text: t(`${PREFIX}.noModuleSelected`), warn: true };
    }
    return {
      text:
        scopeSummary.groups > 0
          ? t(`${PREFIX}.selectionSummary`, {
              count: scopeSummary.modules,
              groups: scopeSummary.groups,
            })
          : t(`${PREFIX}.selectionSummaryNoGroup`, { count: scopeSummary.modules }),
      warn: false,
    };
  })();

  return (
    <div>
      <h2 className="text-2xl font-medium mb-2 mt-2 xl:mt-4">{t(`${PREFIX}.sectionTitle`)}</h2>
      <h3 className="py-3 mb-2">{t(`${PREFIX}.sectionSubtitle`)}</h3>

      <div className="completely-centeredProfile">
        <IsLoading componentId={LRS_COMPONENT_ID} showSuccess={false} />
      </div>

      <OrganizationInputStyle
        title={t(`${PREFIX}.statusLabel`)}
        hint={t(`${PREFIX}.statusHint`)}
        currUserId={currentUser?.id}
        setDisabled={() => undefined}
        hidden
        customButton={statusActions}
      >
        <div className="flex-auto min-w-0 text-sm text-center sm:text-left">
          <p className={`font-medium ${isPaused ? "text-amber-600" : ""}`}>{statusLabel}</p>
          <p className={`text-xs text-gray-500 ${status && isConfigured ? "" : "invisible"}`}>
            {status
              ? t(`${PREFIX}.outboxSummary`, {
                  pending: status.outbox.pending.count + status.outbox.sending.count,
                  sent: status.outbox.sent.count,
                  dead: status.outbox.dead.count,
                })
              : "-"}
            {config?.last_success_at
              ? ` · ${t(`${PREFIX}.lastSuccess`, {
                  date: new Date(config.last_success_at).toLocaleString(locale),
                })}`
              : ""}
          </p>
          <p
            data-testid="lrs-status-detail"
            className={`text-xs ${statusDetail?.warn ? "text-amber-600" : "text-gray-500"} ${
              statusDetail ? "" : "invisible"
            }`}
          >
            {statusDetail?.text ?? "-"}
          </p>
        </div>
      </OrganizationInputStyle>

      <OrganizationInputStyle
        title={t(`${PREFIX}.connection`)}
        hint={t(`${PREFIX}.connectionHint`)}
        currUserId={currentUser?.id}
        setDisabled={() => undefined}
        hidden
        customButton={
          isConfigured ? (
            <div className="flex items-center whitespace-nowrap">
              <div className={separatorClassName}>
                <ButtonText
                  type="button"
                  disabled={testing}
                  text={testing ? t(`${PREFIX}.testing`) : t(`${PREFIX}.testButton`)}
                  onClick={runConnectionTest}
                />
              </div>
              <ButtonText
                type="button"
                text={t("general.edit")}
                onClick={() => setConnectionOpen(true)}
              />
            </div>
          ) : (
            <button
              type="button"
              className="btn-primary-fill whitespace-nowrap"
              onClick={() => setConnectionOpen(true)}
            >
              {t(`${PREFIX}.configure`)}
            </button>
          )
        }
      >
        <div className="flex-auto min-w-0 text-sm text-center sm:text-left">
          {isConfigured && config ? (
            <>
              <p className="font-medium truncate" title={config.endpoint ?? ""}>
                {config.endpoint}
              </p>
              <p className="text-xs text-gray-500">
                {t(`${PREFIX}.credentialsSummary`, { last4: config.secret_last4 ?? "" })}
                {` · xAPI ${config.xapi_version}`}
              </p>
              <p
                data-testid="lrs-connection-test"
                className={`text-xs ${lastTest?.ok ? "text-green-600" : "text-amber-600"} ${
                  lastTest ? "" : "invisible"
                }`}
              >
                {lastTest ? lrsTestMessage(t, lastTest) : "-"}
              </p>
            </>
          ) : (
            <p className="text-gray-500">{t(`${PREFIX}.connectionNotConfigured`)}</p>
          )}
        </div>
      </OrganizationInputStyle>

      {isConfigured && config ? (
        <>
          <OrganizationInputStyle
            title={t(`${PREFIX}.dataLabel`)}
            hint={t(`${PREFIX}.dataHint`)}
            currUserId={currentUser?.id}
            setDisabled={() => undefined}
            hidden
            customButton={
              <ButtonText
                type="button"
                text={t("general.edit")}
                onClick={() => setDataOpen(true)}
              />
            }
          >
            <div className="flex-auto min-w-0 text-sm text-center sm:text-left">
              <p>
                {config.actor_mode === "email"
                  ? t(`${PREFIX}.actorEmailSummary`)
                  : t(`${PREFIX}.actorPseudonymSummary`)}
                {config.actor_mode_locked_at ? (
                  <span className="text-xs text-gray-500">{` · ${t(
                    `${PREFIX}.lockedShort`,
                  )}`}</span>
                ) : null}
              </p>
              <p>
                {config.anonymous_mode === "per_session"
                  ? t(`${PREFIX}.anonymousPerSessionSummary`)
                  : t(`${PREFIX}.anonymousIgnoreSummary`)}
              </p>
              <p>
                {config.include_navigation
                  ? t(`${PREFIX}.navigationOnSummary`)
                  : t(`${PREFIX}.navigationOffSummary`)}
              </p>
            </div>
          </OrganizationInputStyle>

          <OrganizationInputStyle
            title={t(`${PREFIX}.scope`)}
            hint={t(`${PREFIX}.scopeHint`)}
            currUserId={currentUser?.id}
            setDisabled={() => undefined}
            hidden
            customButton={
              <ButtonText
                type="button"
                text={t(`${PREFIX}.chooseModules`)}
                onClick={() => setPickerOpen(true)}
              />
            }
          >
            <p
              className={`flex-auto min-w-0 text-sm text-center sm:text-left ${
                scopeText.warn ? "text-amber-600" : ""
              }`}
            >
              {scopeText.text}
            </p>
          </OrganizationInputStyle>

          <OrganizationInputStyle
            title={t(`${PREFIX}.historyLabel`)}
            hint={t(`${PREFIX}.replayHint`)}
            currUserId={currentUser?.id}
            setDisabled={() => undefined}
            hidden
            customButton={
              <ButtonText
                type="button"
                className="whitespace-nowrap"
                text={t(`${PREFIX}.replay`)}
                onClick={openReplay}
              />
            }
          >
            <p className="flex-auto min-w-0 text-sm text-center sm:text-left">
              {config.emit_from
                ? t(`${PREFIX}.historySince`, {
                    date: new Date(config.emit_from).toLocaleDateString(locale),
                  })
                : t(`${PREFIX}.historySinceActivation`)}
            </p>
          </OrganizationInputStyle>

          <OrganizationInputStyle
            title={t(`${PREFIX}.documentsLabel`)}
            hint={t(`${PREFIX}.documentsHint`)}
            currUserId={currentUser?.id}
            setDisabled={() => undefined}
            hidden
            customButton={
              <div className="flex items-center whitespace-nowrap">
                <div className={separatorClassName}>
                  <FloatingTooltip content={t(`${PREFIX}.profileHint`)} wrap>
                    <ButtonText
                      type="button"
                      text={t(`${PREFIX}.downloadProfile`)}
                      onClick={() => download("profile")}
                    />
                  </FloatingTooltip>
                </div>
                <div className={separatorClassName}>
                  <FloatingTooltip content={t(`${PREFIX}.pseudonymsHint`)} wrap>
                    <ButtonText
                      type="button"
                      text={t(`${PREFIX}.downloadPseudonyms`)}
                      onClick={() => download("pseudonyms")}
                    />
                  </FloatingTooltip>
                </div>
                <FloatingTooltip content={t(`${PREFIX}.modulesHint`)} wrap>
                  <ButtonText
                    type="button"
                    text={t(`${PREFIX}.downloadModules`)}
                    onClick={() => download("modules")}
                  />
                </FloatingTooltip>
              </div>
            }
          >
            <p className="flex-auto min-w-0 text-sm text-gray-500 text-center sm:text-left">
              {t(`${PREFIX}.documentsSummary`)}
            </p>
          </OrganizationInputStyle>
        </>
      ) : null}

      <LrsConnectionDialog
        isOpen={connectionOpen}
        config={config}
        onClose={() => setConnectionOpen(false)}
        onSaved={(test, enabled) => {
          setConnectionOpen(false);
          setLastTest(test);
          dispatch(
            setAlert({
              type: "success",
              msg: t(enabled ? "alerts.lrsExportEnabled" : "alerts.lrsConnectionSaved"),
            }),
          );
          void refreshStatus();
        }}
      />
      <LrsDataDialog
        isOpen={dataOpen}
        config={config}
        onClose={() => setDataOpen(false)}
        onSaved={() => {
          setDataOpen(false);
          dispatch(setAlert({ type: "success", msg: t("alerts.lrsConfigSaved") }));
          void refreshStatus();
        }}
      />
      <ModuleScopePicker
        isOpen={pickerOpen}
        mode={config?.scope_mode ?? "all"}
        selected={selectedProjects}
        pgList={projectGroups.pgList}
        loading={projectGroups.loading}
        error={projectGroups.error}
        onClose={() => setPickerOpen(false)}
        onConfirm={confirmScope}
      />
      <ModalConfirmation
        id={DISABLE_CONFIRM_ID}
        title={t(`${PREFIX}.disableConfirmTitle`)}
        text={t(`${PREFIX}.disableConfirmText`)}
        submitButtonText={t(`${PREFIX}.disableShort`)}
        danger
        focusCancel
        onClickSubmit={confirmDisable}
      />
      <ModalConfirmation
        id={REPLAY_CONFIRM_ID}
        title={t(`${PREFIX}.replayDialogTitle`)}
        text={t(`${PREFIX}.replayDialogText`)}
        submitButtonText={t(`${PREFIX}.replaySubmit`)}
        onClickSubmit={confirmReplay}
      >
        <div className="flex items-center gap-3 mb-4">
          <label className="text-sm font-medium text-gray-700" htmlFor="lrs-replay-date">
            {t(`${PREFIX}.replayDateLabel`)}
          </label>
          <Input
            id="lrs-replay-date"
            type="date"
            value={replayDate}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setReplayDate(event.target.value)
            }
            className="font-medium bg-white text-gray-600 text-sm max-w-max"
          />
        </div>
      </ModalConfirmation>
    </div>
  );
};

export default LrsExportSection;
